import { useState, useRef, useEffect } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";

export function SearchableSelect({ 
  value, 
  onValueChange, 
  options, 
  placeholder = "Select...",
  className,
  triggerClassName,
  testId
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (open && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
    if (!open) {
      setSearch("");
    }
  }, [open]);

  // Filter options based on search
  const filteredOptions = options.filter(option => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      option.code.toLowerCase().includes(searchLower) ||
      option.name.toLowerCase().includes(searchLower)
    );
  });

  const selectedOption = options.find(opt => opt.code === value);

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          "flex h-11 md:h-12 w-full items-center justify-between rounded-md border border-input bg-input/50 px-3 py-2 text-base md:text-lg ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          triggerClassName
        )}
        data-testid={testId}
      >
        <span className="truncate">
          {selectedOption ? (
            <span>{selectedOption.flag} {selectedOption.name} ({selectedOption.code})</span>
          ) : (
            placeholder
          )}
        </span>
        <ChevronDown className={cn(
          "h-4 w-4 opacity-50 transition-transform duration-200",
          open && "rotate-180"
        )} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-2 w-full rounded-md border border-border bg-popover shadow-lg">
          {/* Search Input - Sticky at top */}
          <div className="sticky top-0 bg-popover border-b border-border p-2 z-10">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search currency..."
                className="w-full h-10 pl-9 pr-3 rounded-md bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-[200px] md:max-h-[300px] overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No currency found.
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.code}
                  type="button"
                  onClick={() => {
                    onValueChange(option.code);
                    setOpen(false);
                  }}
                  className={cn(
                    "relative flex w-full cursor-pointer select-none items-center gap-2 px-3 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground transition-colors",
                    value === option.code && "bg-accent/50"
                  )}
                  data-testid={`${testId}-${option.code}`}
                >
                  <span className="flex-1 text-left">
                    {option.flag} {option.name} ({option.code})
                  </span>
                  {value === option.code && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
